import { useNavigate, useParams } from "react-router-dom"
import dayjs from "dayjs"

import { useGetMonitoringLiveDataQuery } from "store/api/monitoringApi"
import { useAppSelector } from "store/hooks"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"

import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter"
import { getRoundedTemperature } from "utils/getRoundedTemperature"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import parseCelsiusToFahrenheit from "utils/parseCelsiusToFahrenheit"
import { RestrictedAccessWidget } from "components/common/RestrictedAccessWidget"

import { useCustomerDashboard } from "../../CustomerDashboardContext"
import {
  StatusSection,
  StatusSectionContent,
  StatusSectionsContainer,
  StatusSectionTitle,
  StyledWidgetButton,
  SystemStateText,
  TemperatureDelta,
  Widget,
  WidgetButton,
  WidgetContent,
  WidgetTitle,
} from "../styled"

export const SystemStatusSummaryWidget = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const { devices, currentDevice } = useCustomerDashboard()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userTemperatureScale = useAppSelector(state => state.user.userTemperatureScale)
  const isCelsius = userTemperatureScale === "celsius"

  const hasMonitoringAccess = userProfile ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.Monitoring) : false

  const selectedDevice = devices[currentDevice]

  const { data } = useGetMonitoringLiveDataQuery(selectedDevice?.sn || "", {
    skip: !selectedDevice?.sn || !hasMonitoringAccess,
    pollingInterval: 30000,
    skipPollingIfUnfocused: true,
  })

  const liveData = data && dayjs().diff(dayjs(data?.updated_at), "minutes") < 6 ? data : null

  const getTemperature = (temperature: number | undefined, parseExactChange = false) => {
    if (temperature === undefined) return "-"

    const parsedTemperature = isCelsius ? temperature : parseCelsiusToFahrenheit(temperature, parseExactChange)
    return `${getRoundedTemperature(parsedTemperature, 1)}${isCelsius ? "°C" : "°F"}`
  }

  const getSystemState = () => {
    if (!liveData) return "-"

    // Check for active cooling/heating stage first
    if ((liveData.current_cooling_stage || 0) > 0) {
      return <SystemStateText $color="#3b92f6">Cooling (Stage {liveData.current_cooling_stage})</SystemStateText>
    }

    if ((liveData.current_heating_stage || 0) > 0) {
      return <SystemStateText $color="#ef4444">Heating (Stage {liveData.current_heating_stage})</SystemStateText>
    }

    // Otherwise show mode
    if (liveData.mode_alias) {
      const mode = liveData.mode_alias.toLowerCase()
      let color = "#0F172A"

      if (mode === "cooling") {
        color = "#3b92f6"
      } else if (mode === "heating") {
        color = "#ef4444"
      } else if (mode === "auto") {
        color = "#f59e0b"
      } else if (mode === "off") {
        color = "#94A3B8"
      }

      return <SystemStateText $color={color}>{capitalizeFirstLetter(mode)}</SystemStateText>
    }

    return "-"
  }

  const handleSeeDetails = () => {
    navigate(`/customers/${id}/livedata`)
  }

  if (!selectedDevice) {
    return null
  }

  return (
    <Widget>
      <WidgetTitle>System Status Summary</WidgetTitle>
      <RestrictedAccessWidget privilege={SubscriptionPrivilege.Monitoring}>
        <WidgetContent>
          <StatusSectionsContainer>
            <StatusSection>
              <StatusSectionTitle>CURRENT TEMP.</StatusSectionTitle>
              <StatusSectionContent>
                {liveData?.current_temperature_embedded !== undefined ? (
                  <>
                    {getTemperature(liveData.current_temperature_embedded)}
                    {liveData.temp_correction ? (
                      <TemperatureDelta>({getTemperature(liveData.temp_correction, true)})</TemperatureDelta>
                    ) : null}
                  </>
                ) : (
                  "-"
                )}
              </StatusSectionContent>
            </StatusSection>
            <StatusSection>
              <StatusSectionTitle>OUTDOOR TEMP</StatusSectionTitle>
              <StatusSectionContent>{getTemperature(liveData?.current_temperature_outdoor)}</StatusSectionContent>
            </StatusSection>
            <StatusSection>
              <StatusSectionTitle>SET TEMPERATURE</StatusSectionTitle>
              <StatusSectionContent>
                {liveData?.mode_alias === "auto" && liveData.auto_temp_low !== null && liveData.auto_temp_high !== null
                  ? `${getTemperature(liveData.auto_temp_low)}-${getTemperature(liveData.auto_temp_high)}`
                  : getTemperature(liveData?.set_temperature)}
              </StatusSectionContent>
            </StatusSection>
            <StatusSection>
              <StatusSectionTitle>SYSTEM STATE</StatusSectionTitle>
              <StatusSectionContent>{getSystemState()}</StatusSectionContent>
            </StatusSection>
          </StatusSectionsContainer>
        </WidgetContent>
        <WidgetButton>
          <StyledWidgetButton onClick={handleSeeDetails} size="auto" color="blueBordered">
            See Details
          </StyledWidgetButton>
        </WidgetButton>
      </RestrictedAccessWidget>
    </Widget>
  )
}
